// Host side of the Program Roadmap macro: mounts the Forge-derived app (built into
// dist/roadmap.<hash>.js) in a same-origin srcdoc iframe and answers its resolver calls
// against the TipTap node that owns it — the Nautilus equivalent of Forge KVS.
//
// The iframe isolates the upstream global CSS (html/body selectors) and its
// single-instance DOM queries, and keeps its keyboard/pointer events away from
// ProseMirror, so the app runs exactly as it does inside Confluence.
import { defaultBoard, normalizeBoard } from './upstream/board.mjs';
import { prepareSave } from './upstream/permissions.mjs';
import { createFeedbackResolvers } from './feedback.js';
import { I18N } from '../../config/i18n.js';

// Replaced at build time with the content-hashed bundle path (see build.js).
const FRAME_SRC = typeof __PROGRAM_ROADMAP_SRC__ !== 'undefined'
    ? __PROGRAM_ROADMAP_SRC__
    : 'dist/roadmap.bundle.js';
const INITIAL_HEIGHT = 640;

const clone = (value) => (value == null ? value : JSON.parse(JSON.stringify(value)));

const escapeAttribute = (value) => String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

const currentColorMode = () => (document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light');

// Nautilus defaults to Spanish; the roadmap follows the interface language.
const currentLanguage = () => {
    const language = window.i18n?.getCurrentLanguage?.() || document.documentElement.lang || 'es';
    return language === 'en' ? 'en' : 'es';
};

// Only the `roadmap.*` strings cross into the iframe, with English as the fallback.
const roadmapMessages = (language) => {
    const messages = {};
    for (const dictionary of [I18N.en, I18N[language] || {}]) {
        for (const [key, value] of Object.entries(dictionary)) {
            if (key.startsWith('roadmap.')) messages[key] = value;
        }
    }
    return messages;
};

// A saved board the server hasn't confirmed yet is also kept in this browser, so a
// reload or a failed save never loses work; it is restored if newer than the page's.
const BACKUP_PREFIX = 'nautilus:roadmap-pending:';
const readBackup = (key) => { try { return JSON.parse(localStorage.getItem(BACKUP_PREFIX + key) || 'null'); } catch { return null; } };
const writeBackup = (key, board) => { try { localStorage.setItem(BACKUP_PREFIX + key, JSON.stringify(board)); } catch { /* storage full or blocked */ } };
const clearBackup = (key) => { try { localStorage.removeItem(BACKUP_PREFIX + key); } catch { /* storage blocked */ } };
// Retry an unconfirmed save with backoff, then keep trying every 30s.
const RETRY_DELAYS = [2000, 5000, 10000, 20000, 30000];

const currentUser = () => {
    const user = window.authSystem?.getCurrentUser?.() || null;
    return {
        name: String(user?.name || user?.username || '').slice(0, 80),
        accountId: String(user?.id || user?.username || '').slice(0, 128)
    };
};

/**
 * Mount one roadmap iframe.
 * @param {object} options
 * @param {string} options.instanceKey  Stable id of this macro (board cache key upstream).
 * @param {() => object|null} options.readBoard  Current board stored on the node.
 * @param {(board: object) => void} options.writeBoard  Put a saved board on the node.
 * @param {() => Promise<boolean>} options.persist  Save the page; resolves to server confirmation.
 * @param {() => ({tasks: object[], projects: object[], openTask: Function, updateTaskDates: Function}|null)} [options.getData]
 *        Nautilus tasks/projects for linking (absent on public pages).
 * @param {boolean} [options.readOnly]  No authenticated save here (public page).
 * @param {string} [options.title]  Accessible iframe title.
 */
export function mountProgramRoadmap({ instanceKey, readBoard, writeBoard, persist, getData = () => null, readOnly = false, title = 'Program roadmap' }) {
    const frame = document.createElement('iframe');
    frame.className = 'docs-program-roadmap__frame';
    frame.title = title;
    frame.style.height = `${INITIAL_HEIGHT}px`;

    let language = currentLanguage();
    let messages = roadmapMessages(language);
    const t = (key) => messages[key] || key;

    const colorListeners = new Set();
    const themeObserver = new MutationObserver(() => {
        const mode = currentColorMode();
        colorListeners.forEach((listener) => listener(mode));
    });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    let retryTimer = 0;
    let retryIndex = 0;
    const notifyFrame = (type) => {
        const view = frame.contentWindow;
        if (view?.CustomEvent) view.dispatchEvent(new view.CustomEvent(type));
    };
    // Ask the page to save; on confirmation drop the local backup, otherwise retry.
    const confirmSaved = async () => {
        clearTimeout(retryTimer);
        let saved = false;
        try { saved = await persist(); } catch { saved = false; }
        if (saved) {
            retryIndex = 0;
            clearBackup(instanceKey);
            return true;
        }
        const delay = RETRY_DELAYS[Math.min(retryIndex, RETRY_DELAYS.length - 1)];
        retryIndex += 1;
        retryTimer = setTimeout(async () => {
            if (await confirmSaved()) notifyFrame('nautilus-roadmap-persisted');
        }, delay);
        return false;
    };

    // The embedded iframe may be clipped by the Docs workspace or browser viewport.
    // Return the ACTUALLY visible portion in coordinates local to the iframe.
    const getVisibleViewport = () => {
        const rect = frame.getBoundingClientRect();
        let clipTop = 0;
        let clipBottom = window.innerHeight || document.documentElement.clientHeight;
        for (let ancestor = frame.parentElement; ancestor; ancestor = ancestor.parentElement) {
            const style = getComputedStyle(ancestor);
            if (!/(auto|scroll|hidden|clip)/.test(style.overflowY)) continue;
            const bounds = ancestor.getBoundingClientRect();
            clipTop = Math.max(clipTop, bounds.top);
            clipBottom = Math.min(clipBottom, bounds.bottom);
        }
        return {
            top: Math.max(0, Math.min(rect.height, clipTop - rect.top)),
            bottom: Math.max(0, Math.min(rect.height, clipBottom - rect.top))
        };
    };
    const onViewportChange = () => notifyFrame('nautilus-roadmap-viewport');
    window.addEventListener('scroll', onViewportChange, true);
    window.addEventListener('resize', onViewportChange);

    const resolvers = {
        getRoadmap: () => {
            let stored = readBoard();
            let pending = false;
            const backup = readOnly ? null : readBackup(instanceKey);
            if (backup && Number(backup.revision) > Number(stored?.revision || 0)) {
                // Work from a save the server never confirmed: put it back and save it.
                writeBoard(backup);
                stored = backup;
                pending = true;
                void confirmSaved().then((saved) => { if (saved) notifyFrame('nautilus-roadmap-persisted'); });
            } else if (backup) {
                clearBackup(instanceKey);
            }
            const board = normalizeBoard(stored || { ...defaultBoard(), title: t('roadmap.board.untitled') }, stored?.revision || 0);
            // Read-only surfaces show the board locked, so no editing affordances appear.
            return { board: readOnly ? { ...board, locked: true } : board, isNew: !stored, pending };
        },
        saveRoadmap: async (payload) => {
            if (readOnly) throw new Error(t('roadmap.error.readOnly'));
            const user = currentUser();
            // Same revision check + creator lock as the Forge resolver. There is no app
            // owner in Nautilus, so the maintainer id is empty (permissions fail closed).
            let board;
            try {
                board = prepareSave(readBoard(), { payload, context: { accountId: user.accountId } }, '', user.name);
            } catch (error) {
                throw new Error(t(/changed elsewhere/.test(error.message) ? 'roadmap.error.conflict' : 'roadmap.error.locked'));
            }
            writeBoard(clone(board));
            writeBackup(instanceKey, board);
            // "Saved" only once the server has the page; until then the app shows the
            // board as not saved and the backup keeps the work.
            const persisted = await confirmSaved();
            return { board, persisted };
        },
        me: () => (readOnly ? { name: '', accountId: '', isMaintainer: false } : { ...currentUser(), isMaintainer: false }),
        // Nautilus tasks and projects the roadmap links to (items ↔ tasks, lanes ↔ projects).
        getNautilusData: () => {
            const data = readOnly ? null : getData();
            return { available: Boolean(data), tasks: data?.tasks || [], projects: data?.projects || [] };
        },
        openTask: ({ id }) => {
            getData()?.openTask?.(id);
            return { ok: true };
        },
        updateTaskDates: async ({ id, startDate, endDate }) => {
            const updateTaskDates = getData()?.updateTaskDates;
            if (typeof updateTaskDates !== 'function') throw new Error('Task updates are unavailable.');
            return updateTaskDates(id, startDate, endDate);
        },
        ...createFeedbackResolvers({ user: currentUser, t })
    };

    frame.__nautilusRoadmapHost = {
        async invoke(name, payload = {}) {
            const resolver = resolvers[name];
            if (!resolver) throw new Error(t('roadmap.feedback.unavailable'));
            // Clone across the realm boundary so the app never mutates node attrs in place.
            return clone(await resolver(clone(payload)));
        },
        getContext: () => ({
            extension: { content: { id: 'nautilus-docs' }, localId: instanceKey },
            environmentType: 'PRODUCTION'
        }),
        getLanguage: () => language,
        getMessages: () => messages,
        getColorMode: currentColorMode,
        onColorModeChange: (listener) => { colorListeners.add(listener); },
        getVisibleViewport,
        setHeight: (height) => {
            if (Number.isFinite(height) && height > 0) frame.style.height = `${height}px`;
        }
    };

    const src = new URL(FRAME_SRC, document.baseURI).href;
    const boot = () => {
        colorListeners.clear();
        frame.srcdoc = `<!doctype html><html lang="${language}" data-color-mode="${currentColorMode()}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>Program Roadmap</title></head><body><main id="app"></main><script type="module" src="${escapeAttribute(src)}"></script></body></html>`;
    };
    boot();

    // The app reads its strings once at boot, so a language switch reloads the frame.
    const languageObserver = new MutationObserver(() => {
        const next = currentLanguage();
        if (next === language) return;
        language = next;
        messages = roadmapMessages(language);
        boot();
    });
    languageObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });

    return {
        frame,
        // The board changed outside the app (document undo, restored version): reload it
        // through the app's own refresh path, which keeps any open dialog intact.
        refresh() {
            notifyFrame('nautilus-roadmap-refresh');
        },
        destroy() {
            clearTimeout(retryTimer);
            window.removeEventListener('scroll', onViewportChange, true);
            window.removeEventListener('resize', onViewportChange);
            themeObserver.disconnect();
            languageObserver.disconnect();
            colorListeners.clear();
            delete frame.__nautilusRoadmapHost;
        }
    };
}
